import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { listAdminUsers, createAdminUser, type AdminUser } from '../../api/admin';
import { ApiError } from '../../api/client';
import { Shield, Phone, Calendar, Plus } from 'lucide-react';

export function AdminUsersPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'admin-users'],
    queryFn: () => listAdminUsers({ pageSize: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createAdminUser({
        username: username.trim(),
        password,
        displayName: displayName.trim() || undefined,
      }),
    onSuccess: () => {
      setCreating(false);
      setUsername('');
      setPassword('');
      setDisplayName('');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '创建失败'),
  });

  const canSubmit = username.trim().length >= 2 && password.length >= 6;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E293B]">平台管理员</h1>
          <p className="mt-1 text-sm text-[#64748B]">
            管理 SaaS 平台超级管理员账号
          </p>
        </div>
        <Button onClick={() => { setCreating(true); setError(''); }}>
          <Plus className="h-4 w-4" />
          添加管理员
        </Button>
      </div>

      {error && !creating && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">{error}</div>
      )}

      {/* 管理员列表 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            管理员列表
            <span className="text-sm font-normal text-[#64748B]">
              共 {data?.total ?? 0} 人
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.rows.length ? (
            <div className="py-12 text-center">
              <Shield className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">暂无管理员账号</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">姓名</th>
                    <th className="pb-3 font-medium">账号</th>
                    <th className="pb-3 font-medium">角色</th>
                    <th className="pb-3 font-medium">创建时间</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.rows.map((user: AdminUser) => (
                    <tr key={user.id} className="text-sm">
                      <td className="py-3">
                        <div className="font-medium text-[#1E293B]">
                          {user.displayName || '未设置'}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1 text-[#64748B]">
                          <Phone className="h-4 w-4" />
                          {user.phone}
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge variant="primary">超级管理员</Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1 text-[#64748B]">
                          <Calendar className="h-4 w-4" />
                          {new Date(user.createdAt).toLocaleDateString('zh-CN')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 添加管理员弹窗 */}
      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="添加平台管理员"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)} disabled={createMutation.isPending}>
              取消
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !canSubmit}
            >
              {createMutation.isPending ? '创建中...' : '创建'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">登录账号 *</label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="如 admin / 手机号"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">登录密码 *</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="至少 6 位"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">姓名</label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="可选"
            />
          </div>
          <p className="text-xs text-[#94A3B8]">
            新账号将拥有平台超级管理员权限，可管理全部租户门店。操作将记录审计日志。
          </p>
        </div>
      </Modal>
    </div>
  );
}
